import React from 'react';
import PropTypes from 'prop-types';
import { formatTalkDateTime } from '../../../helpers/talkDate';
import { talkSpeakers } from '../../../helpers/talkSpeakers';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { Container } from 'semantic-ui-react';
import Sections from '../Helpers/Sections';
import DefaultViewWithoutTitle from '../../Custom/DefaultViewWithoutTitle';
import RelatedItemsDisplay from '../Helpers/RelatedItemsDisplay';
import './Talk.css';
import { vocabTitle } from '../../../helpers/content';
import { flattenRichTextToAppURL } from '../../../helpers/richText';

const TalkView = (props) => {
  const { content } = props;

  // "Master thesis talk · Results": the stage only says something next to
  // the type.
  const typeLabel =
    [vocabTitle(content.type_of_talk), vocabTitle(content.talk_stage)]
      .filter(Boolean)
      .join(' · ') || null;

  const speakers = talkSpeakers(content);
  const hasSpeakerInfo =
    speakers.length > 0 || content.speaker_affiliation || content.speaker_email;

  const hasSchedule = content.talk_start || content.talk_end;
  const hasVenue =
    content.venue_room ||
    content.venue_building ||
    content.venue_address ||
    content.venue_map_link ||
    content.meeting_url;

  const hasInvitation =
    content.invitation?.data && content.invitation.data !== '<p></p>';
  const hasAbstract =
    content.talk_abstract?.data && content.talk_abstract.data !== '<p></p>';

  const hasLinks =
    content.registration_link || content.slides_link || content.recording_link;

  const relatedTopics = Array.isArray(content.related_topics)
    ? content.related_topics.filter(Boolean)
    : [];
  const hasTopics = relatedTopics.length > 0 && !content.hide_related_topics;

  return (
    <Container id="view-wrapper" className="view-wrapper talk-view">
      <div className="view-header">
        {typeLabel && <p className="view-header__subtitle">{typeLabel}</p>}
        <h2 className="view-header__title">{content.title || content.id}</h2>
        {/* No description here. `description` is a listing field - the one
            line that goes on a card and into a search result - and repeating
            it as the first paragraph of the page it summarises says the same
            thing twice. The full view has its own content. */}
        <div className="view-header__divider" />
      </div>
      <Sections>
        {(hasSchedule || content.timezone) && (
          <div className="view-meta-grid">
            <div className="view-meta-grid__col">
              {content.talk_start && (
                <div className="view-section">
                  <h3 className="view-section__label">Start:</h3>
                  <p className="view-section__value">
                    {formatTalkDateTime(content.talk_start, content.timezone)}
                    {content.timezone ? ` (${content.timezone})` : ''}
                  </p>
                </div>
              )}
              {content.talk_end && (
                <div className="view-section">
                  <h3 className="view-section__label">End:</h3>
                  <p className="view-section__value">
                    {formatTalkDateTime(content.talk_end, content.timezone)}
                  </p>
                </div>
              )}
            </div>
            <div className="view-meta-grid__col">
              {content.online_event && (
                <div className="view-section">
                  <h3 className="view-section__label">Format:</h3>
                  <p className="view-section__value">Online / hybrid</p>
                </div>
              )}
            </div>
          </div>
        )}

        {hasVenue && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Location:</h3>
              <p className="view-section__value">
                {content.venue_room}
                {content.venue_room && content.venue_building ? ', ' : ''}
                {content.venue_building}
              </p>
              {content.venue_address && (
                <p className="view-section__value view-section__value--lines">
                  {content.venue_address}
                </p>
              )}
              <div className="view-section__buttons">
                {content.venue_map_link && (
                  <a
                    href={content.venue_map_link}
                    className="view-section__button"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Map
                  </a>
                )}
                {content.meeting_url && (
                  <a
                    href={content.meeting_url}
                    className="view-section__button"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Join Online
                  </a>
                )}
              </div>
            </div>
          </>
        )}

        {hasInvitation && (
          <>
            <div className="view-section">
              <div
                className="view-section__richtext"
                dangerouslySetInnerHTML={{
                  __html: flattenRichTextToAppURL(content.invitation.data),
                }}
              />
            </div>
          </>
        )}

        {hasAbstract && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Abstract:</h3>
              <div
                className="view-section__richtext"
                dangerouslySetInnerHTML={{
                  __html: flattenRichTextToAppURL(content.talk_abstract.data),
                }}
              />
            </div>
          </>
        )}

        {hasSpeakerInfo && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Speaker:</h3>
              {speakers.map((speaker) => (
                <p key={speaker.key} className="view-section__value">
                  {speaker.href ? (
                    <UniversalLink href={speaker.href}>
                      {speaker.name}
                    </UniversalLink>
                  ) : (
                    speaker.name
                  )}
                </p>
              ))}
              {content.speaker_affiliation && (
                <p className="view-section__value">
                  {content.speaker_affiliation}
                </p>
              )}
              {content.speaker_email && (
                <p className="view-section__value">
                  <a href={`mailto:${content.speaker_email}`}>
                    {content.speaker_email}
                  </a>
                </p>
              )}
            </div>
          </>
        )}

        {hasLinks && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Links:</h3>
              <div className="view-section__buttons">
                {content.registration_link && (
                  <a
                    href={content.registration_link}
                    className="view-section__button"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Registration
                  </a>
                )}
                {content.slides_link && (
                  <a
                    href={content.slides_link}
                    className="view-section__button"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Slides
                  </a>
                )}
                {content.recording_link && (
                  <a
                    href={content.recording_link}
                    className="view-section__button"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Recording
                  </a>
                )}
              </div>
            </div>
          </>
        )}

        {hasTopics && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Related Topics:</h3>
              <RelatedItemsDisplay
                items={relatedTopics}
                mode={content.related_topics_display}
                kind="plain"
                fieldKey={`${content['@id']}-related_topics`}
              />
            </div>
          </>
        )}
      </Sections>

      <DefaultViewWithoutTitle {...props} />
    </Container>
  );
};

TalkView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    id: PropTypes.string,
    description: PropTypes.string,
    talk_stage: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ title: PropTypes.string }),
    ]),
    type_of_talk: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ title: PropTypes.string }),
    ]),
    talk_start: PropTypes.string,
    talk_end: PropTypes.string,
    timezone: PropTypes.string,
    speaker: PropTypes.string,
    speaker_affiliation: PropTypes.string,
    speaker_email: PropTypes.string,
    related_speakers: PropTypes.array,
    online_event: PropTypes.bool,
    venue_room: PropTypes.string,
    venue_building: PropTypes.string,
    venue_address: PropTypes.string,
    venue_map_link: PropTypes.string,
    meeting_url: PropTypes.string,
    invitation: PropTypes.shape({ data: PropTypes.string }),
    talk_abstract: PropTypes.shape({ data: PropTypes.string }),
    registration_link: PropTypes.string,
    slides_link: PropTypes.string,
    recording_link: PropTypes.string,
    hide_related_topics: PropTypes.bool,
    related_topics_display: PropTypes.string,
    related_topics: PropTypes.array,
  }).isRequired,
};

export default TalkView;
